import { Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useDeleteAgent } from '../../hooks/useAgents';
import { Agent } from '../../types';
import DeleteResourceControl from '../shared/DeleteResourceControl';
import { usedByLabel } from '../../utils/formatUtils';

interface AgentCardProps {
  agent: Agent;
}

export default function AgentCard({ agent }: AgentCardProps) {
  const deleteAgent = useDeleteAgent();

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/agents/${agent.id}`} className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                {agent.name}
              </h3>
              {agent.isActive ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500" title="Active" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-gray-400" title="Inactive" />
              )}
            </div>
            {/* The default Model under the name (task 1311). */}
            <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400" data-testid="agent-card-model">
              {agent.modelConfig
                ? `${agent.modelConfig.model.modelId} · ${agent.modelConfig.model.provider?.key ?? 'unknown'}`
                : 'Service default'}
            </p>
            {agent.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                {agent.description}
              </p>
            )}
            <div className="mt-2 flex items-center space-x-4">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                agent.isActive
                  ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}>
                {agent.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400" data-testid="agent-card-used-by">
            Used by {usedByLabel(agent.usedByCount)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Updated {new Date(agent.updatedAt).toLocaleDateString()}
          </div>
        </div>
      </Link>

      {/* One delete affordance for the whole app (task 864): the flag, the
          dialog, the dependents preview, the mutation and the toast in one
          place. The inline two-step swap this replaces said nothing about
          what else used the resource, and offered no way back. */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <DeleteResourceControl
          flag="innovate.fe.agents.delete"
          entity="agents"
          entityLabel="agent"
          name={agent.name}
          id={agent.id}
          stopPropagation
          body={<p>The prompts, MCP connections and knowledge bases attached to it stay in your library.</p>}
          onDelete={() => deleteAgent.mutateAsync(agent.id)}
        />
      </div>
    </div>
  );
}
